import { type ApolloClient } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { type GetStaticPropsContext } from 'next'
import { REVALIDATE_INTERVAL_IN_SECONDS } from 'constants/common'
import { EXPERT_DETAIL_TAB_KEY_ROUTES } from 'constants/routes'
import { prepareBreadcrumbsProps } from 'routes/utils/prepareGetStaticBreadcrumbsProps'
import { AvailableExpertDetailTabsByExpertSlugDocument } from 'types/graphql-generated'
import { initializeApollo } from 'utils/apolloClient'

const errorReturnValue = {
  notFound: true,
  props: { slug: null, navigation: [], breadcrumbs: [] },
  revalidate: REVALIDATE_INTERVAL_IN_SECONDS,
}

export const expertTabsGetStaticProps = async (
  context: GetStaticPropsContext,
  { callback, currentTab },
  inputApolloClient?: ApolloClient<any>
) => {
  const { slug } = context.params

  if (Array.isArray(slug)) {
    return errorReturnValue
  }

  const apolloClient = inputApolloClient || initializeApollo()

  try {
    const { data: expertQueryData } = await apolloClient.query({
      query: AvailableExpertDetailTabsByExpertSlugDocument,
      variables: {
        slug,
      },
    })
    const availableTabs = expertQueryData.expertBySlug.availableExpertDetailTabs

    // redirect to the first available tab if the current tab is not available
    if (!availableTabs.includes(currentTab)) {
      const destination = EXPERT_DETAIL_TAB_KEY_ROUTES[availableTabs[0]]({
        slug,
      })
      return {
        redirect: {
          destination,
          permanent: false,
        },
      }
    }

    const breadcrumbs = await prepareBreadcrumbsProps(
      apolloClient,
      EXPERT_DETAIL_TAB_KEY_ROUTES[currentTab]({ slug })
    )

    return callback(
      context,
      { breadcrumbs, slug, expertId: expertQueryData.expertBySlug.id },
      apolloClient
    )
  } catch (error) {
    datadogRum.addError(error)
    return errorReturnValue
  }
}
